import { CSSProperties, useEffect, useMemo } from "react";
import { Button, Form } from "@dreambigger/design-system/src/components";
import {
  FinancialBrand,
  Entry,
  CustomCollectionsStepAssets,
  AutoPopulateConfig,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import { message, Progress } from "antd";
import dayjs from "dayjs";
import extractCustomFields from "../../../utils/extractCustomFields";
import resolveCustomStepTitles, {
  ResolvedCustomStepTitles,
} from "../../../utils/resolveCustomStepTitles";
import mergeEntryHeader from "../utils/mergeEntryHeader";
import compileTemplate from "../../../utils/compileTemplate";
import validateCrossEntryRules from "../utils/validateCrossEntryRules";
import shouldDisplayField from "../utils/shouldDisplayField";
import SectionedCustomFieldGrid from "./SectionedCustomFieldGrid";
import { CrossEntryValidation } from "../types";
import animations from "@dreambigger/shared/src/components/board/styles/animations.module.scss";

// Field types whose stored values need dayjs coercion before being handed to
// Antd's DatePicker. Must stay in sync with DateFieldConfig["fieldType"] in
// @dreambigger/shared/src/types/custom-field.ts.
const DATE_FIELD_TYPES: ReadonlySet<string> = new Set(["date", "dateInput"]);

interface EntryCardProps {
  entry: Entry;
  customFields?: CustomCollectionsStepAssets["customFields"];
  crossEntryValidations?: CrossEntryValidation[];
  brand: FinancialBrand;
  application?: OnboardingApplication;
  autoPopConfig?: AutoPopulateConfig;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onSave: (data: Partial<Entry>) => Promise<void>;
  onDelete?: () => void;
  /** Called when the user cancels editing. Defaults to reset-form + collapse.
   * Callers can override to handle unsaved-blank cleanup or other policy. */
  onCancel?: () => void;
  displayConfig?: ResolvedCustomStepTitles;
  getUniqueConflict?: (
    entryId: number,
    fieldKey: string,
    value: string
  ) => string | null;
  getCrossEntryTotal?: (fieldKey: string, excludeId?: number) => number;
  getCrossEntryCount?: (fieldKey: string, excludeId?: number) => number;
}

export default function EntryCard({
  entry,
  customFields,
  crossEntryValidations,
  brand,
  application,
  autoPopConfig,
  isExpanded,
  onExpand,
  onCollapse,
  onSave,
  onDelete,
  onCancel,
  displayConfig,
  getUniqueConflict,
  getCrossEntryTotal,
  getCrossEntryCount,
}: EntryCardProps) {
  const dc = displayConfig || resolveCustomStepTitles();
  const [form] = Form.useForm();

  const fieldConfigs = useMemo(
    () => extractCustomFields(customFields),
    [customFields]
  );

  const autoPopulatedKeys = useMemo(
    () =>
      autoPopConfig
        ? new Set(Object.keys(autoPopConfig.fieldMapping))
        : undefined,
    [autoPopConfig]
  );

  const toFormValues = (src: Partial<Entry>): Partial<Entry> => {
    const out: Partial<Entry> = { ...src };
    for (const cf of fieldConfigs) {
      if (
        DATE_FIELD_TYPES.has(cf.fieldType) &&
        out[cf.fieldKey] &&
        !dayjs.isDayjs(out[cf.fieldKey])
      ) {
        out[cf.fieldKey] = dayjs(out[cf.fieldKey] as string);
      }
    }
    return out;
  };

  const initialValues = useMemo(() => toFormValues(entry), [entry]);

  // Sync form store with current entry data when the card expands.
  // The form instance (from useForm) persists across Form mount/unmount cycles,
  // so the store can have stale values from a previous edit session.
  useEffect(() => {
    if (isExpanded) {
      form.setFieldsValue(initialValues);
    }
  }, [isExpanded, initialValues]);

  const handleFinish = async (values: Partial<Entry>) => {
    const cleanedValues: Partial<Entry> = { ...values };

    for (const field of fieldConfigs) {
      if (
        !shouldDisplayField(
          field,
          cleanedValues,
          application,
          undefined,
          fieldConfigs
        )
      ) {
        cleanedValues[field.fieldKey] = undefined;
      }
    }

    if (crossEntryValidations) {
      const validationError = validateCrossEntryRules({
        entryId: entry.id,
        values: cleanedValues,
        validations: crossEntryValidations,
        getUniqueConflict,
        getCrossEntryTotal,
        getCrossEntryCount,
        application,
        allFieldConfigs: fieldConfigs,
      });
      if (validationError) {
        message.error(validationError);
        return;
      }
    }

    try {
      await onSave(cleanedValues);
      onCollapse();
    } catch {
      message.error("Failed to save. Please try again.");
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    form.setFieldsValue(entry);
    onCollapse();
  };

  const incomplete = useMemo((): boolean => {
    if (fieldConfigs.length === 0) return false;

    for (const field of fieldConfigs) {
      if (!field.fieldRequired) continue;
      if (
        !shouldDisplayField(field, entry, application, undefined, fieldConfigs)
      )
        continue;
      if (autoPopulatedKeys?.has(field.fieldKey)) continue;

      if (
        entry[field.fieldKey] === undefined ||
        entry[field.fieldKey] === null ||
        entry[field.fieldKey] === ""
      ) {
        return true;
      }
    }
    return false;
  }, [fieldConfigs, entry, application, autoPopulatedKeys]);

  const maxTotalProgress = useMemo((): {
    total: number;
    max: number;
  } | null => {
    if (!crossEntryValidations || !getCrossEntryTotal) return null;
    const maxTotalRule = crossEntryValidations.find(
      (v): v is Extract<CrossEntryValidation, { rule: "maxTotal" }> =>
        v.rule === "maxTotal"
    );
    if (!maxTotalRule) return null;

    // Gate visibility on whether the rule's field is visible for this entry —
    // this recovers the old "only show for entries that participate" behavior
    // without encoding any domain-specific gate field.
    const ruleField = fieldConfigs.find(
      (f) => f.fieldKey === maxTotalRule.fieldKey
    );
    if (
      ruleField &&
      !shouldDisplayField(
        ruleField,
        entry,
        application,
        undefined,
        fieldConfigs
      )
    )
      return null;

    const othersTotal = getCrossEntryTotal(maxTotalRule.fieldKey, entry.id);
    const thisValue = Number(entry[maxTotalRule.fieldKey]) || 0;
    return { total: othersTotal + thisValue, max: maxTotalRule.value };
  }, [
    crossEntryValidations,
    getCrossEntryTotal,
    fieldConfigs,
    application,
    entry,
  ]);

  // Card style: primary color left-accent only on expanded card, light gray otherwise
  const baseCardStyle: CSSProperties = {
    borderRadius: 6,
    borderLeft: `3px solid ${isExpanded ? brand.primaryColor : "#e5e7eb"}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    background: "#fff",
  };

  // Resolve the five-slot header: step-level defaults with per-autoPop overrides.
  const header = useMemo(
    () => mergeEntryHeader(dc.entryHeader, autoPopConfig?.entryHeader),
    [dc.entryHeader, autoPopConfig?.entryHeader]
  );
  const expandedTitle = compileTemplate(header.expandedTitle, entry);

  // --- EXPANDED MODE ---
  if (isExpanded) {
    const hiddenKeys = autoPopConfig ? autoPopulatedKeys : undefined;

    return (
      <div
        className={`pv-4 ph-4 mb-3 ${animations.fadeIn_f}`}
        style={baseCardStyle}
      >
        {expandedTitle && (
          <p className="f-3 fwb mb-3" style={{ color: brand.primaryColor }}>
            {expandedTitle}
          </p>
        )}

        <Form form={form} onFinish={handleFinish} initialValues={initialValues}>
          {customFields && customFields.length > 0 && (
            <SectionedCustomFieldGrid
              customFields={customFields}
              hiddenFieldKeys={hiddenKeys}
              form={form}
              brand={brand}
              application={application}
            />
          )}

          <div className="flex items-center mt-4 pt-3">
            <Button
              type="primary"
              htmlType="submit"
              className="bn br-0 h-5 ph-4 mr-3"
            >
              Save
            </Button>
            <Button
              type="link"
              onClick={handleCancel}
              className="f-2 pa-0 h-auto"
              style={{ color: brand.primaryColor }}
            >
              Cancel
            </Button>
            {onDelete && (
              <Button
                type="link"
                onClick={onDelete}
                className="f-2 pa-0 h-auto ml-auto"
                style={{ color: brand.primaryColor }}
              >
                Delete
              </Button>
            )}
          </div>
        </Form>
      </div>
    );
  }

  // --- COLLAPSED MODE ---
  const topline = compileTemplate(header.topline, entry);
  const title = compileTemplate(header.title, entry);
  const titleAppend = compileTemplate(header.titleAppend, entry);
  const subtitle = compileTemplate(header.subtitle, entry);

  return (
    <div
      className="pv-3 ph-4 mb-3 subtleLift"
      style={{ ...baseCardStyle, cursor: "pointer" }}
      onClick={onExpand}
    >
      <div className="flex justify-between items-center">
        <div>
          {topline && (
            <span
              className="fwb ttu f-1 db"
              style={{ color: brand.primaryColor, letterSpacing: "0.05em" }}
            >
              {topline}
            </span>
          )}
          <span className="f-3 fwb">{title}</span>
          {titleAppend && (
            <span className="f-2 gray-7 ml-2">{titleAppend}</span>
          )}
          {subtitle && <p className="f-2 gray-7 mb-0 mt-1">{subtitle}</p>}
          {incomplete && (
            <p className="f-1 mb-0 mt-1" style={{ color: "#d97706" }}>
              {dc.entryIncompleteText}
            </p>
          )}
          {maxTotalProgress && (
            <div className="mt-1" style={{ maxWidth: 180 }}>
              <Progress
                percent={
                  maxTotalProgress.max > 0
                    ? (maxTotalProgress.total / maxTotalProgress.max) * 100
                    : 0
                }
                showInfo={false}
                strokeColor={
                  maxTotalProgress.total > maxTotalProgress.max
                    ? "#ff4d4f"
                    : brand.primaryColor
                }
                size="small"
              />
              <span className="f-1 gray-6">
                {maxTotalProgress.total}% / {maxTotalProgress.max}%
              </span>
            </div>
          )}
        </div>
        <Button
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          style={{ color: brand.primaryColor }}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}
